import {
  collection, doc, setDoc, getDoc, getDocs, updateDoc, deleteDoc,
  query, where, orderBy, limit, serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { thumbFromDataUrl } from '../utils/imageCompress';
import {
  COLLECTIONS, GALLERY_LIMITS, GALLERY_FETCH_MAX, ITEM_KINDS,
  serializeProductFields, blobIdsOf,
} from '../constants/gallery';
// Publishing, hiding or deleting an item changes what the dashboard feed can
// draw from, so its cached pool has to be dropped here — beside the writes,
// rather than trusting every caller to remember.
import { invalidateFeedWork } from './socialService';
// A listing's own fields are what product search filters on, so publishing or
// editing one makes those cached pools wrong too.
import { invalidateProducts } from './productService';

// ── GALLERY SERVICE ──────────────────────────────────────────────────────────
// Publishable items: photos and 3D designs. They behave identically — picture,
// description, visibility switch, delete — so ONE implementation serves both and
// the `kind` argument only chooses which collection pair to touch. Two parallel
// copies of this logic is how the two would drift apart.
//
// STORAGE SHAPE. Each item is TWO documents:
//   <meta>/{id}  — description, visible, thumbUrl, ownerUid   (listed in bulk)
//   <blob>/{id}  — fullUrl                                    (fetched on open)
//
// Firebase Storage isn't provisioned on this project, so images live in
// Firestore as compressed data URLs. Splitting them this way is what keeps that
// viable: rendering a 24-photo gallery reads 24 small metadata docs (~30KB
// thumbnails) instead of 24 full-size images. If Storage is enabled later, only
// the blob half needs to change — the metadata shape and every caller stay put.
//
// VISIBILITY. `visible: false` hides an item from everyone except its owner.
// That is enforced in security rules, not here (see firestore.rules) — a client
// -side filter alone would let anyone read hidden items straight from the API.

const cols = (kind) => {
  const c = COLLECTIONS[kind];
  if (!c) throw new Error(`unknown gallery kind: ${kind}`);
  return c;
};

const clean = (s, max) => String(s || '').trim().slice(0, max);

/**
 * Creates a gallery item. `image` is the output of compressPhoto() — or an
 * ARRAY of them, for a post of up to five pictures.
 *
 * THE BLOBS ARE WRITTEN FIRST, all of them: if one fails there is no metadata
 * document pointing at pictures that do not exist, which is the harmless
 * failure ordering. The reverse leaves a broken tile in the grid.
 *
 * The FIRST image is the cover — its thumbnail is the only one on the metadata
 * document (see blobIdsOf in constants/gallery.js) and its blob keeps the
 * item's own id, so a one-image post is byte-for-byte what it always was.
 */
export async function addGalleryItem(kind, ownerUid, image, fields = {}) {
  const { meta, blob } = cols(kind);
  const id = doc(collection(db, meta)).id;      // client-side id, so both docs share it

  const images = (Array.isArray(image) ? image : [image])
    .filter(Boolean)
    .slice(0, GALLERY_LIMITS.imagesPerItem);
  if (!images.length) throw new Error('no-image');

  // The cover keeps the item's id; the rest get their own. Sequential rather
  // than parallel: each is a ~200KB document, and a phone firing five at once
  // is how a slow connection turns into five timeouts instead of one retry.
  const blobIds = [];
  for (let i = 0; i < images.length; i++) {
    const blobId = i === 0 ? id : doc(collection(db, blob)).id;
    await setDoc(doc(db, blob, blobId), {
      ownerUid,
      fullUrl: images[i].fullUrl,
      createdAt: serverTimestamp(),
    });
    blobIds.push(blobId);
  }

  const cover = images[0];
  const metaDoc = {
    ownerUid,
    kind,
    // In order, cover first. This is the carousel's running order and the
    // delete path's list of what to clean up.
    blobIds,
    title:       clean(fields.title, GALLERY_LIMITS.title),
    description: clean(fields.description, GALLERY_LIMITS.description),
    visible:     fields.visible !== false,       // published by default
    // THE COVER'S THUMBNAIL, and only the cover's — see blobIdsOf. Carrying
    // all five here would multiply the cost of every listing query by five to
    // show pictures nobody has opened yet.
    thumbUrl:    cover.thumbUrl,
    width:       cover.width || null,
    height:      cover.height || null,
    // Only meaningful for designs — links the published card back to the
    // project it was rendered from.
    projectId:   fields.projectId || null,
    // Only meaningful for shops — price and where to buy. Always present so
    // every document has the same shape, and null everywhere else.
    ...serializeProductFields(fields),
    createdAt:   serverTimestamp(),
  };
  await setDoc(doc(db, meta, id), metaDoc);
  invalidateFeedWork();
  invalidateProducts();
  return { id, ...metaDoc };
}

/**
 * Lists a user's items. `ownerView` = true is the owner managing their own
 * gallery (hidden items included); false is a visitor (visible only).
 *
 * NOTE the ordering: a visitor's query filters on `visible` AND orders by
 * `createdAt`, which Firestore serves from a composite index. If that index is
 * missing the call fails with a console link to create it — see the README note
 * in firestore.rules.
 */
export async function listGalleryItems(kind, ownerUid, ownerView = false) {
  const { meta } = cols(kind);
  const clauses = [where('ownerUid', '==', ownerUid)];
  if (!ownerView) clauses.push(where('visible', '==', true));

  const snap = await getDocs(query(
    collection(db, meta),
    ...clauses,
    orderBy('createdAt', 'desc'),
    // The fetch ceiling, NOT the owner's plan cap — this call has no idea whose
    // gallery it is reading and should not grow a plan lookup to find out. See
    // the note on GALLERY_FETCH_MAX.
    limit(GALLERY_FETCH_MAX),
  ));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * One item's METADATA by id — thumbnail, description, price, visibility.
 *
 * The listing queries above are all scoped to an owner, which is right for a
 * gallery but useless to a caller holding only an id: the basket stores photo
 * ids and has to resolve them across many different shops.
 *
 * Returns null when the document is missing OR unreadable. A hidden item is
 * refused by the rules, and that refusal is the correct answer to "can this
 * person see this product" — it has to surface as "not available" rather than
 * as an uncaught FirebaseError.
 */
export async function getGalleryItem(kind, id) {
  if (!id) return null;
  const { meta } = cols(kind);
  try {
    const snap = await getDoc(doc(db, meta, id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch {
    return null;
  }
}

/** Fetches the full-size rendition for one item. Called on open, never in bulk. */
export async function getGalleryFull(kind, id) {
  const { blob } = cols(kind);
  const snap = await getDoc(doc(db, blob, id));
  return snap.exists() ? snap.data().fullUrl : null;
}

/** Description / title / visibility. Only ever touches the metadata document. */
export async function updateGalleryItem(kind, id, patch) {
  const { meta } = cols(kind);
  const next = {};
  if (patch.description !== undefined) next.description = clean(patch.description, GALLERY_LIMITS.description);
  if (patch.title !== undefined)       next.title = clean(patch.title, GALLERY_LIMITS.title);
  if (patch.visible !== undefined)     next.visible = !!patch.visible;
  // Product fields move TOGETHER, all of them, whenever any one is present.
  // A price without its currency renders wrong; a type without its subtype is
  // half a classification; and a subtype left behind after the type changed is
  // a listing that no filter can reach. The serializer resolves the whole set
  // consistently, so it is handed the whole set.
  const PRODUCT_KEYS = ['type', 'subtype', 'color', 'price', 'currency', 'productUrl'];
  if (PRODUCT_KEYS.some(k => patch[k] !== undefined)) {
    Object.assign(next, serializeProductFields(patch));
  }
  if (!Object.keys(next).length) return;
  next.updatedAt = serverTimestamp();
  await updateDoc(doc(db, meta, id), next);
  // A description edit is harmless, but a visibility flip changes what the
  // feed may show — one invalidation covers both rather than branching.
  invalidateFeedWork();
  invalidateProducts();
}

/**
 * Attaches more pictures to a post that already exists.
 *
 * Blobs first, then the list — the same ordering the create path uses and for
 * the same reason: a `blobIds` entry pointing at a document that was never
 * written is a carousel slide that can only ever be blank, while an unreferenced
 * blob is invisible.
 *
 * Returns the new `blobIds` so the caller can update its copy without a re-read.
 */
export async function addItemImages(kind, id, ownerUid, images) {
  const { meta, blob } = cols(kind);
  const list = (images || []).filter(Boolean);
  if (!id || !list.length) return null;

  const snap = await getDoc(doc(db, meta, id));
  if (!snap.exists()) return null;
  const current = blobIdsOf({ id, ...snap.data() });

  const room = GALLERY_LIMITS.imagesPerItem - current.length;
  if (room <= 0) return current;

  const added = [];
  for (const image of list.slice(0, room)) {
    const blobId = doc(collection(db, blob)).id;
    await setDoc(doc(db, blob, blobId), {
      ownerUid,
      fullUrl: image.fullUrl,
      createdAt: serverTimestamp(),
    });
    added.push(blobId);
  }

  const next = [...current, ...added];
  await updateDoc(doc(db, meta, id), { blobIds: next, updatedAt: serverTimestamp() });
  invalidateFeedWork();
  invalidateProducts();
  return next;
}

/**
 * Detaches one picture from a post. ANY picture, including the cover.
 *
 * ── REMOVING THE COVER PROMOTES THE NEXT ONE ────────────────────────────────
 * The metadata document carries the COVER'S thumbnail and no others (see
 * blobIdsOf), which is what every grid in the app draws a post with. So
 * dropping the first picture is not a list edit — it needs a new thumbnail,
 * and the only copy of the promoted picture is the full rendition in its blob.
 * It is fetched, re-encoded to thumbnail size, and written alongside the new
 * list, so the grid keeps working and shows the picture that is now first.
 *
 * That work is why this was refused at first. It is worth doing: a post that
 * began with one picture and grew is exactly the case where the original turns
 * out to be the wrong one to lead with, and "delete the whole post" is a poor
 * answer to that.
 *
 * IF THE THUMBNAIL CANNOT BE MADE, NOTHING IS REMOVED. A post whose cover is
 * gone and whose thumbnail still points at it is broken everywhere at once;
 * leaving the picture in place is the recoverable failure.
 *
 * Returns the new `blobIds`, or null when the removal was refused.
 */
export async function removeItemImage(kind, id, blobId) {
  const { meta, blob } = cols(kind);
  if (!id || !blobId) return null;

  const snap = await getDoc(doc(db, meta, id));
  if (!snap.exists()) return null;
  const current = blobIdsOf({ id, ...snap.data() });

  // A post is its pictures; the last one cannot go. Deleting the post is that.
  if (current.length < 2) return null;
  if (!current.includes(blobId)) return null;

  const next = current.filter(b => b !== blobId);
  const patch = { blobIds: next, updatedAt: serverTimestamp() };

  if (current[0] === blobId) {
    const promoted = await getGalleryFull(kind, next[0]);
    if (!promoted) return null;
    const thumb = await thumbFromDataUrl(promoted);
    if (!thumb?.thumbUrl) return null;
    patch.thumbUrl = thumb.thumbUrl;
    patch.width = thumb.width;
    patch.height = thumb.height;
  }

  await updateDoc(doc(db, meta, id), patch);
  invalidateFeedWork();
  invalidateProducts();

  // After the list, so a failed delete leaves an orphan rather than a slide
  // pointing at nothing.
  await deleteDoc(doc(db, blob, blobId)).catch(err => {
    // eslint-disable-next-line no-console
    console.warn('[gallery] image detached but blob not deleted:', blobId, err?.code || err?.message);
  });
  return { blobIds: next, thumbUrl: patch.thumbUrl || null };
}

/** Convenience wrapper for the visibility switch. */
export function setGalleryItemVisible(kind, id, visible) {
  return updateGalleryItem(kind, id, { visible });
}

/**
 * Deletes the metadata and EVERY blob behind it. Metadata goes first so the
 * item disappears from every listing immediately; a blob that then fails to
 * delete is invisible and orphaned rather than a tile that renders but can't
 * be opened.
 */
export async function deleteGalleryItem(kind, id) {
  const { meta, blob } = cols(kind);

  // WHICH BLOBS TO REMOVE HAS TO BE READ BEFORE THE METADATA GOES — the list
  // lives on the metadata document, so deleting that first (right for
  // everything else) would strand every image but the cover. One extra read,
  // and only on delete.
  let blobIds = [id];
  try {
    const snap = await getDoc(doc(db, meta, id));
    if (snap.exists()) blobIds = blobIdsOf({ id, ...snap.data() });
  } catch { /* fall back to the cover, which is always at the item's own id */ }

  await deleteDoc(doc(db, meta, id));
  invalidateFeedWork();
  invalidateProducts();

  // Independently, so one blob refusing to go does not leave the other four
  // behind.
  await Promise.all(blobIds.map(bid =>
    deleteDoc(doc(db, blob, bid)).catch(err => {
      // eslint-disable-next-line no-console
      console.warn('[gallery] blob not deleted (metadata is gone):', bid, err?.code || err?.message);
    })));
}

export { ITEM_KINDS };
